// ============================================================================
// Timer Start
// ============================================================================

class FlexibleTimer {
  constructor(
    element,
    callback
  ) {
    this.callback = callback; // Function to call when the timer completes
    this.remainingTime = 0; // Time left in milliseconds
    this.startTime = 0; // Timestamp when the timer starts or resumes
    this.timerId = null; // Reference to the timer
    this.isRunning = false; // Timer state
    this.totalDuration = 0; // Total duration of the timer
    this.progressBar = typeof element === "string" ? document.querySelector(element) : element;
    this.updateProgressBar();
  }

  // Start or resume the timer
  start(duration = null) {
    if (duration !== null) {
      this.remainingTime = duration;
      this.totalDuration = duration; // Save the total duration for progress calculations
    }

    // console.log(`start this.remainingTime = ${this.remainingTime}`);
    if (this.remainingTime <= 0) {
      // this.end();
      // console.error("Timer duration must be greater than 0");
      return;
    }

    if (this.isRunning) {
      // console.warn("Timer is already running");
      return;
    }

    // console.info('timer start');

    this.startTime = Date.now();
    this.isRunning = true;

    this.timerId = setInterval(() => {
      const remaining = this.getRemainingTime();
      if (remaining <= 0) {
        this.end();
      } else {
        // console.log(`remaining = ${remaining}`);
        // console.log(`this.remainingTime = ${this.remainingTime}`);
        this.updateProgressBar();
      }
    }, 100); // Update the progress bar every 100ms
  }

  end() {
    if (this.timerId) {
      clearInterval(this.timerId);
    }

    this.isRunning = false;
    this.remainingTime = 0;

    if (this.callback) {
      this.callback();
    }

    this.updateProgressBar();
  }

  // Pause the timer
  pause() {
    if (!this.isRunning) {
      // console.warn("Timer is not running");
      return;
    }

    // console.info('timer pause');

    clearInterval(this.timerId);
    this.isRunning = false;
    this.remainingTime -= Date.now() - this.startTime;
    this.updateProgressBar();
  }

  // Reset the timer with a new duration
  reset(newDuration) {
    this.pause(); // Stop the current timer
    this.remainingTime = newDuration;
    this.totalDuration = newDuration;
    this.updateProgressBar();
  }

  // Check the remaining time in milliseconds
  getRemainingTime() {
    if (this.isRunning) {
      return this.remainingTime - (Date.now() - this.startTime);
    }
    return this.remainingTime;
  }

  // Stop the timer completely
  stop() {
    clearInterval(this.timerId);
    this.isRunning = false;
    this.remainingTime = 0;
    this.updateProgressBar();
  }

  // Update the width of the progress bar
  updateProgressBar() {
    if (this.progressBar) {
      const remaining = this.getRemainingTime();
      let percentage = 0;

      if (remaining > 0) {
        percentage = ((this.totalDuration - remaining) / this.totalDuration) * 100;
      }

      this.progressBar.style.width = `${percentage}%`;
    }
  }
}

// ============================================================================
// Timer End
// ============================================================================

// ============================================================================
// Slideshow Start
// ============================================================================

// ----------------------------------------------------------------------------
// Slideshow Variables
// ----------------------------------------------------------------------------

const SLIDESHOW_AUTO_DELAY = 5000;
const SLIDESHOW_AUTO_DELAY_INTERACTED = 10000;
const SLIDESHOW_AUTO_DELAY_MIN = 2000;
const SLIDESHOW_VIEWPORT_ROOT_MARGIN = '80px 0px';
const SLIDESHOW_VIEWPORT_THRESHOLD = 0.01;
const SLIDESHOW_MEDIA_BLUR_START = 1;
const SLIDESHOW_MEDIA_BLUR_END = 50;
const SLIDESHOW_MEDIA_BLUR_MAX = 10;
const SLIDESHOW_MEDIA_EFFECT_RETRY_MAX = 6;
const ENABLE_SLIDESHOW_MEDIA_EFFECTS = true;
const slideshowMediaEffectRetries = new WeakMap();

// Swipe detection variables
const SWIPE_THRESHOLD = 1; // Minimum movement to consider it a swipe

function clearSlideshowMediaEffect(frame) {
  frame.classList.remove(
    'slideshow-media-frame--enhanced',
    'slideshow-media-frame--landscape',
    'slideshow-media-frame--portrait'
  );
  frame.style.removeProperty('--slideshow-media-fill-url');
  frame.style.removeProperty('--slideshow-media-offset-x');
  frame.style.removeProperty('--slideshow-media-offset-y');
  frame.style.removeProperty('--slideshow-media-blur');
}

function getSlideshowMediaBlur(letterboxSize) {
  if (letterboxSize <= SLIDESHOW_MEDIA_BLUR_START) {
    return 0;
  }

  if (letterboxSize >= SLIDESHOW_MEDIA_BLUR_END) {
    return SLIDESHOW_MEDIA_BLUR_MAX;
  }

  const progress = (letterboxSize - SLIDESHOW_MEDIA_BLUR_START) / (SLIDESHOW_MEDIA_BLUR_END - SLIDESHOW_MEDIA_BLUR_START);
  return progress * SLIDESHOW_MEDIA_BLUR_MAX;
}

function isSlideshowMediaEffectEnabled(frame) {
  const slideshow = frame.closest('.slideshow');

  if (!slideshow) {
    return false;
  }

  return slideshow.dataset.slideshowLetterbox === 'blur' || slideshow.classList.contains('slideshow--letterbox-blur');
}

function scheduleSlideshowMediaEffectRetry(frame) {
  const retryCount = slideshowMediaEffectRetries.get(frame) || 0;

  if (retryCount >= SLIDESHOW_MEDIA_EFFECT_RETRY_MAX) {
    return;
  }

  slideshowMediaEffectRetries.set(frame, retryCount + 1);

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      updateSlideshowMediaEffect(frame);
    });
  });
}

function updateSlideshowMediaEffect(frame) {
  if (!ENABLE_SLIDESHOW_MEDIA_EFFECTS || !isSlideshowMediaEffectEnabled(frame)) {
    clearSlideshowMediaEffect(frame);
    return;
  }

  const image = frame.querySelector('img');
  const imageUrl = image?.currentSrc || image?.src;

  if (!image || !imageUrl) {
    return;
  }

  const frameRect = frame.getBoundingClientRect();
  const frameWidth = frameRect.width;
  const frameHeight = frameRect.height;

  if (frameWidth <= 0 || frameHeight <= 0 || image.naturalWidth <= 0 || image.naturalHeight <= 0) {
    scheduleSlideshowMediaEffectRetry(frame);
    return;
  }

  const widthScale = frameWidth / image.naturalWidth;
  const heightScale = frameHeight / image.naturalHeight;
  const containScale = Math.min(widthScale, heightScale);
  const renderedWidth = image.naturalWidth * containScale;
  const renderedHeight = image.naturalHeight * containScale;
  const offsetX = Math.max(0, (frameWidth - renderedWidth) * 0.5);
  const offsetY = Math.max(0, (frameHeight - renderedHeight) * 0.5);
  const hasSideLetterboxing = offsetX > offsetY;
  const activeLetterboxSize = hasSideLetterboxing ? offsetX : offsetY;
  const blurAmount = getSlideshowMediaBlur(activeLetterboxSize);

  slideshowMediaEffectRetries.delete(frame);
  frame.style.setProperty('--slideshow-media-fill-url', `url("${imageUrl}")`);
  frame.style.setProperty('--slideshow-media-offset-x', `${offsetX}px`);
  frame.style.setProperty('--slideshow-media-offset-y', `${offsetY}px`);
  frame.style.setProperty('--slideshow-media-blur', `${blurAmount}px`);
  frame.classList.add('slideshow-media-frame--enhanced');
  frame.classList.toggle('slideshow-media-frame--landscape', !hasSideLetterboxing);
  frame.classList.toggle('slideshow-media-frame--portrait', hasSideLetterboxing);
}

// ----------------------------------------------------------------------------
// Slideshow Class
// ----------------------------------------------------------------------------

class Slideshow {
  constructor(element) {
    // ----------------------------------------------------------------------------
    // Slideshow Class Variables
    // ----------------------------------------------------------------------------
    
    this.interacted = false;

    // Bind methods to ensure correct context
    this.onSlideshowSlideChange = this.onSlideshowSlideChange.bind(this);
    this.onTimerComplete = this.onTimerComplete.bind(this);
    this.timerStart = this.timerStart.bind(this);
    this.onSlideshowInit = this.onSlideshowInit.bind(this);
    this.scheduleSlideshowRelayout = this.scheduleSlideshowRelayout.bind(this);
    this.relayoutSlideshow = this.relayoutSlideshow.bind(this);
    this.dispatchSlideshowLayoutSettled = this.dispatchSlideshowLayoutSettled.bind(this);
    this.handleSlideshowIntersection = this.handleSlideshowIntersection.bind(this);
    this.updateSlideshowMediaEffects = this.updateSlideshowMediaEffects.bind(this);
    this.setupSlideshowMutationObserver = this.setupSlideshowMutationObserver.bind(this);
    this.setupSlideshowLinks = this.setupSlideshowLinks.bind(this);

    this.elementSlideshowWrap = element;
    this.elementSlideshowInteract = element.querySelector('.slideshow-interact');
    this.elementSlideshow = element.querySelector('.slideshow-items');
    this.elementSlideshowPrev = element.querySelector('.slideshow-prev');
    this.elementSlideshowNext = element.querySelector('.slideshow-next');

    this.elementSlideshowButtons = [
      this.elementSlideshowPrev,
      this.elementSlideshowNext
    ];
    this.slideshowRelayoutFrame = null;
    this.slideshowResizeObserver = null;
    this.slideshowIntersectionObserver = null;
    this.slideshowMutationObserver = null;
    this.lastSlideshowLayoutWidth = 0;
    this.timerPauseReasons = {};

    // ----------------------------------------------------------------------------
    // Slideshow Swipe Detection Variables
    // ----------------------------------------------------------------------------

    this.isInteraction = false; // Tracks if a click or press is active
    this.interactionXInit = 0; // Starting X position of a touch/mouse event
    this.isInteractionSwipe = false; // Tracks whether a swipe is in progress

    // ----------------------------------------------------------------------------
    // Slideshow FlexibleTimer Instance
    // ----------------------------------------------------------------------------

    // this.timer = null;

    // Initialize the FlexibleTimer
    this.timer = new FlexibleTimer(element.querySelector('.slideshow-progress'), this.onTimerComplete);
    this.setupSlideshowViewportObserver();

    // ----------------------------------------------------------------------------
    // Slideshow Siema Instance
    // ----------------------------------------------------------------------------

    // Initialize Siema with bound methods
    this.slideshowSiema = new Siema({
      // selector: '.slideshow-items',
      selector: this.elementSlideshow,
      duration: 500,
      easing: 'ease-out',
      perPage: this.getSlideshowPerPageConfig(),
      startIndex: 0,
      draggable: true,
      multipleDrag: true,
      threshold: 50,
      loop: true,
      rtl: false,
      onInit: this.onSlideshowInit,
      onChange: this.onSlideshowSlideChange.bind(this)
    });

    this.lastSlideshowLayoutWidth = this.getSlideshowLayoutWidth();

    // ----------------------------------------------------------------------------
    // Event Listeners
    // ----------------------------------------------------------------------------

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    // Event Listeners - Binds
    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    // Bind the method to retain `this` context
    this.isEventOnElement = this.isEventOnElement.bind(this);

    this.slideshowTimerStart = this.slideshowTimerStart.bind(this);
    this.slideshowTimerPause = this.slideshowTimerPause.bind(this);

    this.handleInteractStart = this.handleInteractStart.bind(this);
    this.handleInteractMove = this.handleInteractMove.bind(this);
    this.handleInteractEnd = this.handleInteractEnd.bind(this);
    this.handleInteractEndTouch = this.handleInteractEndTouch.bind(this);

    this.handleButtonAction = this.handleButtonAction.bind(this);

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    // Event Listeners - Wrap
    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    this.elementSlideshowInteract.addEventListener('mouseover', (event) => {
      // alert("mouseover");
      if (this.isTouchDevice() || !this.isEventOnElement(this.elementSlideshowInteract, event)) {
        return;
      }

      // alert("mouseover");
      this.slideshowTimerPause();
    });

    this.elementSlideshowInteract.addEventListener('mouseout', (event) => {
      if (!this.isEventOnElement(this.elementSlideshowInteract, event)) {
        return;
      }

      // alert("mouseout");
      this.handleInteractEnd(event);
      this.slideshowTimerStart();
    });

    // this.elementSlideshowInteract.addEventListener('focusin', (event) => {
    // });

    // this.elementSlideshowInteract.addEventListener('focusout', (event) => {
    // });

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    // Event Listeners - Swipe Detection
    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    // Attach event listeners for swipe detection
    this.elementSlideshow.addEventListener('touchstart', this.handleInteractStart);
    this.elementSlideshow.addEventListener('touchmove', this.handleInteractMove);
    this.elementSlideshow.addEventListener('touchend', this.handleInteractEndTouch);

    this.elementSlideshow.addEventListener('mousedown', this.handleInteractStart);
    this.elementSlideshow.addEventListener('mousemove', this.handleInteractMove);
    // elementSlideshow.addEventListener('mouseup', this.handleInteractEnd);

    this.elementSlideshow.addEventListener('click', this.handleInteractEnd);
    // this.elementSlideshowPrev.addEventListener('focusin', this.handleInteractStart);
    // this.elementSlideshowNext.addEventListener('focusout', this.handleInteractEnd);
    // this.elementSlideshow.addEventListener('focusout', (event) => {
    //   const newFocusTarget = event.relatedTarget;
    //   if (!this.elementSlideshow.contains(newFocusTarget)) {
    //     // Focus has completely left this.elementSlideshow and its children
    //     this.handleInteractEnd();
    //   }
    // });

    this.setupSlideshowLinks();

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    // Event Listeners - Buttons
    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    this.elementSlideshowPrev.addEventListener('click', this.handleButtonAction(() => this.slideshowSiema.prev()));
    this.elementSlideshowNext.addEventListener('click', this.handleButtonAction(() => this.slideshowSiema.next()));

    this.elementSlideshowButtons.forEach((elementSlideshowButton) => {
      elementSlideshowButton.addEventListener('touchstart', () => {
        this.slideshowTimerPause();
      });
    });

    // this.elementSlideshowInteract.addEventListener('touchstart', () => {
    //   this.slideshowTimerPause();
    // });
    this.elementSlideshowInteract.addEventListener('touchend', () => {
      this.slideshowTimerStart();
    });

    this.elementSlideshowInteract.addEventListener('transitionend', () => {
      this.scheduleSlideshowRelayout();
    });

    this.setupSlideshowMediaEffects();
    this.setupSlideshowMutationObserver();

    if ('ResizeObserver' in window) {
      this.slideshowResizeObserver = new ResizeObserver(() => {
        this.scheduleSlideshowRelayout();
      });
      this.slideshowResizeObserver.observe(this.elementSlideshowWrap);
    }

    window.addEventListener('load', () => {
      this.scheduleSlideshowRelayout();
    });

    // this.elementSlideshowInteract.addEventListener('contextmenu', () => {
    //   alert("contextmenu");
    // });

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    // Event Listeners - Visibility
    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    // Ensure Timer On Load Begin (Mobile Issue)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        // Page is now active
        this.setTimerPauseReason('page', false);
        this.slideshowTimerStart(SLIDESHOW_AUTO_DELAY);
      } else {
        // Page is hidden, optionally pause the timer
        this.setTimerPauseReason('page', true);
      }
    });
  };

  // ----------------------------------------------------------------------------
  // Slideshow Timer
  // ----------------------------------------------------------------------------

  timerStart(duration = 0) {
    if (!this.timer) {
      return;
    }

    if (duration > 0) {
      if (!this.canRunTimer()) {
        this.timer.reset(duration);
        return;
      }

      this.timer.start(duration);
      return;
    }

    if (!this.canRunTimer()) {
      return;
    }

    if (this.timer.remainingTime > 0) {
      if (this.timer.remainingTime < SLIDESHOW_AUTO_DELAY_MIN) {
        this.timer.remainingTime = SLIDESHOW_AUTO_DELAY_MIN;
      }
      this.timer.start();
    }
    else {
      this.timer.start(SLIDESHOW_AUTO_DELAY);
      this.interacted = false;
    }
  }

  canRunTimer() {
    return Object.keys(this.timerPauseReasons).length === 0;
  }

  setTimerPauseReason(reason, paused, updateTimer = true) {
    if (paused) {
      this.timerPauseReasons[reason] = true;
    } else {
      delete this.timerPauseReasons[reason];
    }

    if (!updateTimer) {
      return;
    }

    if (this.canRunTimer()) {
      this.timerStart();
    } else {
      this.timerPause();
    }
  }

  setupSlideshowViewportObserver() {
    if (!('IntersectionObserver' in window)) {
      return;
    }

    this.setTimerPauseReason('viewport', true, false);

    this.slideshowIntersectionObserver = new IntersectionObserver(this.handleSlideshowIntersection, {
      root: null,
      rootMargin: SLIDESHOW_VIEWPORT_ROOT_MARGIN,
      threshold: SLIDESHOW_VIEWPORT_THRESHOLD
    });

    this.slideshowIntersectionObserver.observe(this.elementSlideshowWrap);
  }

  handleSlideshowIntersection(entries) {
    const entry = entries[0];
    const isVisible = entry && entry.isIntersecting && entry.intersectionRatio >= SLIDESHOW_VIEWPORT_THRESHOLD;

    this.setTimerPauseReason('viewport', !isVisible);

    if (isVisible) {
      this.scheduleSlideshowRelayout();
    }
  }

  timerPause() {
    if (this.timer) {
      this.timer.pause();
    }
  }

  onTimerComplete() {
    // console.log("Timer completed!");
    this.slideshowSiema.next();
    this.timerStart();
  }

  // ----------------------------------------------------------------------------
  // Slideshow Init and Change
  // ----------------------------------------------------------------------------

  onSlideshowInit() {
    this.elementSlideshow.classList.remove('slideshow-items--init');
    this.updateSlideshowMediaEffects();
    this.timerStart();
  }

  onSlideshowSlideChange() {
    // console.log(`Slide Selector: ${this.slideshowSiema.config.selector}, Current Slide: ${this.slideshowSiema.currentSlide}`);
    this.scheduleSlideshowRelayout();
  }

  // ----------------------------------------------------------------------------
  // Slideshow Layout
  // ----------------------------------------------------------------------------

  parsePositiveInteger(value) {
    const parsedValue = parseInt(value, 10);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
  }

  getSlideshowPerPageConfig() {
    const desktopPerPage = this.parsePositiveInteger(this.elementSlideshowWrap.dataset.perPage);
    const tabletPerPage = this.parsePositiveInteger(this.elementSlideshowWrap.dataset.perPageTablet);
    const phonePerPage = this.parsePositiveInteger(this.elementSlideshowWrap.dataset.perPagePhone);

    if (!desktopPerPage && !tabletPerPage && !phonePerPage) {
      return 1;
    }

    if (!tabletPerPage && !phonePerPage) {
      return desktopPerPage;
    }

    return {
      0: phonePerPage || 1,
      640: tabletPerPage || desktopPerPage || phonePerPage || 1,
      1024: desktopPerPage || tabletPerPage || phonePerPage || 1
    };
  }

  getSlideshowLayoutWidth() {
    const rect = this.elementSlideshowInteract.getBoundingClientRect();
    return Math.round(rect.width || this.elementSlideshow.offsetWidth || 0);
  }

  scheduleSlideshowRelayout() {
    if (this.slideshowRelayoutFrame) {
      return;
    }

    this.slideshowRelayoutFrame = requestAnimationFrame(() => {
      this.slideshowRelayoutFrame = requestAnimationFrame(() => {
        this.slideshowRelayoutFrame = null;
        this.relayoutSlideshow();
      });
    });
  }

  relayoutSlideshow() {
    if (!this.slideshowSiema || typeof this.slideshowSiema.resizeHandler !== 'function') {
      return;
    }

    const currentWidth = this.getSlideshowLayoutWidth();
    if (!(currentWidth > 0)) {
      return;
    }

    const didRelayout = currentWidth !== this.lastSlideshowLayoutWidth;
    if (didRelayout) {
      this.lastSlideshowLayoutWidth = currentWidth;
      this.slideshowSiema.resizeHandler();
    }

    this.updateSlideshowMediaEffects();
    this.dispatchSlideshowLayoutSettled(currentWidth, didRelayout);
  }

  dispatchSlideshowLayoutSettled(width, didRelayout) {
    this.elementSlideshowWrap.dispatchEvent(new CustomEvent('slideshow:layout-settled', {
      bubbles: true,
      detail: {
        slideshow: this,
        width,
        didRelayout
      }
    }));
  }

  getSlideshowMediaFrames() {
    return Array.from(this.elementSlideshowWrap.querySelectorAll('.slideshow-media-frame'));
  }

  setupSlideshowLinks() {
    for (const elem of this.elementSlideshowWrap.querySelectorAll('a')) {
      if (elem.dataset.slideshowLinkBound === 'true') {
        continue;
      }

      elem.dataset.slideshowLinkBound = 'true';
      elem.addEventListener('focusin', () => {
        this.slideshowTimerPause();
        this.scheduleSlideshowRelayout();
      });
      elem.addEventListener('focusout', (event) => {
        const newFocusTarget = event.relatedTarget;

        if (!this.elementSlideshowWrap.contains(newFocusTarget)) {
          this.handleInteractEnd(event);
          this.slideshowTimerStart();
        }

        this.scheduleSlideshowRelayout();
      });
    }
  }

  setupSlideshowMutationObserver() {
    if (!('MutationObserver' in window) || this.slideshowMutationObserver) {
      return;
    }

    this.slideshowMutationObserver = new MutationObserver(() => {
      this.setupSlideshowLinks();
      this.setupSlideshowMediaEffects();
      this.scheduleSlideshowRelayout();
    });

    this.slideshowMutationObserver.observe(this.elementSlideshow, {
      childList: true,
      subtree: true
    });
  }

  setupSlideshowMediaEffects() {
    this.getSlideshowMediaFrames().forEach((frame) => {
      const image = frame.querySelector('img');

      if (!image || image.dataset.slideshowMediaEffectBound === 'true') {
        return;
      }

      image.dataset.slideshowMediaEffectBound = 'true';
      image.addEventListener('load', () => {
        updateSlideshowMediaEffect(frame);
        this.scheduleSlideshowRelayout();
      });

      if (image.complete) {
        updateSlideshowMediaEffect(frame);
      }
    });
  }

  updateSlideshowMediaEffects() {
    this.setupSlideshowLinks();
    this.setupSlideshowMediaEffects();
    this.getSlideshowMediaFrames().forEach((frame) => {
      updateSlideshowMediaEffect(frame);
    });
  }

  // ----------------------------------------------------------------------------
  // Slideshow Interact
  // ----------------------------------------------------------------------------

  onSlideshowInteract() {
    this.interacted = true;

    if (this.timer) {
      this.timer.reset(SLIDESHOW_AUTO_DELAY_INTERACTED);
    }
  }

  slideshowButtonAction(action) {
    this.onSlideshowInteract();

    action(); // Perform the action (e.g., `slideshowSiema.prev()` or `slideshowSiema.next()`)
  }

  slideshowTimerPause() {
    // console.log('mouseover wrap');
    this.setTimerPauseReason('interaction', true);
  }

  slideshowTimerStart(duration = 0) {
    // console.log('mouseout wrap');
    this.setTimerPauseReason('interaction', false, false);

    if (duration > 0) {
      this.timerStart(duration);
      return;
    }

    this.timerStart();
  }

  // ----------------------------------------------------------------------------
  // Slideshow Swipe Detection
  // ----------------------------------------------------------------------------

  setIsInteractionSwipe(value) {
    this.isInteractionSwipe = value; // Set swipe status

    // console.log(`this.isInteractionSwipe = ${this.isInteractionSwipe}`);

    if (this.isInteractionSwipe) {
      this.elementSlideshowWrap.classList.add('slideshow-items--swipe');
    } else {
      this.elementSlideshowWrap.classList.remove('slideshow-items--swipe');
    }
  }

  getEventInteract(event) {
    return event.touches ? event.touches[0] : event;
  }

  // Detect the start of a interaction event
  handleInteractStart(event) {
    // Update interacting state
    if (event.type === 'mousedown' || event.type === 'touchstart') {
      this.isInteraction = true;
      this.interactionXInit = this.getEventInteract(event).clientX;
    }

    // console.log('[Swipe] Start');
    this.setIsInteractionSwipe(false);

    this.slideshowTimerPause();
  }

  // Detect the movement during a touch/mouse event
  handleInteractMove(event) {
    if (!this.isInteraction || this.isInteractionSwipe) return; // Only proceed if an interaction, and not a swipe, is active

    const deltaX = this.getEventInteract(event).clientX - this.interactionXInit;

    // Check if the movement is more horizontal than vertical and exceeds the threshold
    if (Math.abs(deltaX) > SWIPE_THRESHOLD) {
      // console.log('[Swipe] Move');
      this.setIsInteractionSwipe(true);
    }
  }

  isInteractLink(event) {
    let target = event.target;

    // Traverse up the DOM to check for parent <a> tag
    while (target && target !== this.elementSlideshow) {
      if (target.tagName.toLowerCase() === 'a') {
        return true; // Exit the while after preventing the click
      }

      target = target.parentElement;
    }

    return false;
  }

  handleSwipeEnd(event) {
    // Prevent clicks on links if a swipe occurred
    if (this.isInteractionSwipe) {
      if (this.isInteractLink(event)) {
        event.preventDefault(); // Prevent default click behavior
        event.stopPropagation(); // Stop the event from propagating further

        // console.warn('Click on link prevented');
      }

      this.setIsInteractionSwipe(false);

      // console.warn('No clickable link detected');
    }
  }

  // Detect the end of a touch/mouse event
  handleInteractEnd(event) {
    // console.log('[Swipe] End');
    this.handleSwipeEnd(event);

    if (this.isInteraction) {
      this.onSlideshowInteract();
    }

    this.isInteraction = false;
  }

  // ----------------------------------------------------------------------------
  // Event Listeners
  // ----------------------------------------------------------------------------

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Event Listeners - Wrap
  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  isEventOnElement(element, event) {
    return !element.contains(event.relatedTarget);
  }

  // Utility function to check for touch support
  isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  handleInteractEndTouch(event) {
    if (!this.isEventOnElement(this.elementSlideshowInteract, event)) {
      return;
    }
    // alert("touchend");

    this.handleInteractEnd(event);
    this.slideshowTimerStart();
  }

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Event Listeners - Buttons
  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  handleButtonAction(action) {
    return () => {
      this.slideshowButtonAction(() => action());

      if (!this.isTouchDevice()) {
        return;
      }

      this.slideshowTimerStart();
    };
  }
}

// ============================================================================
// Slideshow End
// ============================================================================

// ============================================================================
// Slideshow Setup Begin
// ============================================================================

const elementSlideshowWraps = document.querySelectorAll('.slideshow');
let slideshows = []

for (const elementSlideshowWrap of elementSlideshowWraps) {
  slideshows.push(new Slideshow(elementSlideshowWrap));
  // break;
  // console.log("Total slideshows: ", slideshows.length);
}

// ============================================================================
// Slideshow Setup End
// ============================================================================
